import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { OutlinedButton } from "agm-ui";

// hooks and utilities
import { useLanguage, useTranslateFunction } from "lang/Translate";
import useAirport, { useAirportImage, useAirportType, useHub } from "hooks/useAirport";
import { routerOptions, useRedirectNew } from "hooks/useRedirect";
import { useTabIdNew } from "hooks/useTabId";
import useRemember from "hooks/useRemember";
import formatMoney from "common/format";

// classes
import Slot from "classes/slot";

// components
import DemandTab from "./DemandTab";
import AircraftTab from "./AircraftTab";
import SideWindowTab from "../SideWindowTab";
import BallGridPulseLoader from "components/common/BallGridPulseLoader/BallGridPulseLoader";

// assets
import countriesList from "assets/json/countriesList.json";

// styles
import "./AirportPopup.scoped.scss";

function AirportPopup() {
	const airportId = useTabIdNew("airport", { ...routerOptions.get("s"), isNumber: true });
	const rememberedAirportId = useRemember(airportId);
	const airport = useAirport(rememberedAirportId);

	const translate = useTranslateFunction("SideWindow->AirportPopup");
	const redirect = useRedirectNew(routerOptions.get("w"));
	const currLanguage = useLanguage();

	const [nameInOtherLanguages, setNameInOtherLanguages] = useState(new Map());
	const hub = useHub();
	const [name, setName] = useState(airport.name);
	const image = useAirportImage(airport.wikipedia_link);
	const airportType = useAirportType(airport.type);

	const getNameInOtherLanguage = useCallback(async () => {
		if (!airport.wikipedia_link) {
			setName(airport.name);
			return;
		}

		const splitted = airport.wikipedia_link.split("/");
		const title = splitted[splitted.length - 1];
		const response = await fetch(
			`https://en.wikipedia.org/w/api.php?action=query&format=json&prop=langlinks&titles=${title}&utf8=1&lllimit=500&origin=*&redirects=1`
		);
		const json = await response.json();

		try {
			const availableLanguages = Object.values(json.query?.pages)[0]?.langlinks;
			const availableLanguagesMap = new Map(availableLanguages.map(o => [o.lang, o["*"]]));
			setName(availableLanguagesMap.get(currLanguage.toLowerCase()) ?? airport.name);
			setNameInOtherLanguages(availableLanguagesMap);
		} catch (err) {
			setName(airport.name);
		}
	}, [airport, currLanguage]);

	const redirectToWikipedia = () => {
		if (!nameInOtherLanguages.size === 0) {
			window.open(airport.wikipedia_link, "_blank");
			return;
		}

		const translation = nameInOtherLanguages.get(currLanguage.toLowerCase());
		if (!translation) {
			window.open(airport.wikipedia_link, "_blank");
			return;
		}

		window.open(`http://${currLanguage.toLowerCase()}.wikipedia.org/wiki/${translation.replace(/ /g, "_")}`, "_blank");
	};

	useEffect(() => {
		getNameInOtherLanguage();
	}, [getNameInOtherLanguage]);

	return (
		<>
			<div className="img_container">
				{image ? <img src={image} alt="" /> : <BallGridPulseLoader color="#14273e" />}
			</div>

			<SideWindowTab text="Informacje" Icon={<i className="fa-solid fa-circle-info" />}>
				<div className="item">
					<div className="heading">{translate("AIRPORT_NAME")}</div>
					<div className="content">{name}</div>
				</div>
				<div className="item">
					<div className="heading">{translate("COUNTRY")}</div>
					<div className="content">
						{
							countriesList.find(c => c.code === airport.iso_country)[
								currLanguage === "PL" ? "name_pl" : "name_en"
							]
						}
						<img
							src={`https://flagcdn.com/${airport.iso_country.toLowerCase()}.svg`}
							alt={currLanguage === "PL" ? "Flaga" : "Flag"}
							className="flag"
						/>
					</div>
				</div>
				<div className="item">
					<div className="heading">{translate("AIRPORT_SIZE")}</div>
					<div className="content">{capitalizeFirstLetter(airportType)}</div>
				</div>
				<div className="item">
					<div className="heading">
						{translate("IATA_CODE")} / {translate("ICAO_CODE")}
					</div>
					<div className="content">
						{airport.iata_code ?? "-"} / {airport.ident ?? "-"}
					</div>
				</div>
				<div className="links">
					<i className="fa-solid fa-book" onClick={redirectToWikipedia} />
					<i
						className="fas fa-link"
						onClick={() => redirect(`aircraftIcon.Routes.slots.airport_details~${airport.ident}`)}
					/>
				</div>
			</SideWindowTab>

			<AircraftTab airportId={rememberedAirportId} />
			{airport.id !== hub.id && <DemandTab />}
			{airport.id !== hub.id && <SlotsTab airport={airport} />}
		</>
	);
}

function SlotsTab({ airport }) {
	const translate = useTranslateFunction("SideWindow->AirportPopup");
	const slots = useSelector(state => state.slots);
	const [[unusedSlots, usedSlots], setSlotsCount] = useState([0, 0]);
	const currLanguage = useLanguage();

	async function buy() {
		if (unusedSlots + usedSlots > 20) {
			alert(
				currLanguage === "PL"
					? "Łączna maksymalna liczba slotów na jednym lotnisku wynosi 20!"
					: "Total maximum number of slots at one airport is 20!"
			);
			return;
		}

		await Slot.buy(airport.id, airport.slot_price);
	}

	useEffect(() => {
		const merged = Slot.getMergedSlots(slots, false, true);
		setSlotsCount(
			merged.reduce(
				(acc, slot) => {
					if (slot.destination !== airport.id) return acc;
					return [slot.unusedAmount, slot.usedAmount];
				},
				[0, 0]
			)
		);
	}, [slots, airport.id]);

	return (
		<SideWindowTab text={translate("SLOTS")} Icon={<i className="fa-solid fa-signature" />} className="slots_tab">
			<div className="item">
				<div className="heading">
					{translate("USED_SLOTS")} / {translate("OWNED_SLOTS")}
				</div>
				<div className="content">
					{usedSlots} / {unusedSlots + usedSlots}
				</div>
			</div>
			<div className="item extra-margin">
				<div className="heading">{translate("SLOT_PRICE")}</div>
				<div className="content">{formatMoney(airport.slot_price)}</div>
			</div>
			<OutlinedButton accent="blue" icon={<i className="fa-solid fa-cart-shopping" />} onClick={buy} text="Buy slot" />
		</SideWindowTab>
	);
}

function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export default AirportPopup;
